/**
 * DDD Domain Seeder
 * 
 * Implements the DomainSeederService pattern for Domain-Driven Design domain.
 * Seeds strategic design (domains, bounded contexts) and tactical design (aggregates)
 * following Eric Evans DDD principles.
 */

import { BaseDomainSeeder } from './shared-seeding/BaseDomainSeeder';
import { DDD_COLLECTIONS } from '../config/collections';
import { DDDDataSeeder } from '../utils/seed-ddd-data';
import { getMongoClient, getMongoConfig } from "@swoft/persistence";

export class DDDSeeder extends BaseDomainSeeder {
  constructor() {
    super('ddd');
  }

  async seedDomain(organizationId: string, projectId: string): Promise<{
    success: boolean;
    metrics: {
      totalCollections: number;
      totalDocuments: number;
      validDocuments: number;
      invalidDocuments: number;
      seedingDurationMs: number;
      validationDurationMs: number;
    };
    error?: string;
  }> {
    const seedingStartTime = Date.now();
    
    try {
      console.log(`📐 Starting ${this.domainName} domain seeding...`);
      
      // Create and run the real DDD data seeder
      const dddSeeder = new DDDDataSeeder();
      await dddSeeder.run();
      
      const seedingDurationMs = Date.now() - seedingStartTime;
      
      // Perform validation
      const validationStartTime = Date.now();
      const validationResult = await this.validateSeededData();
      const validationDurationMs = Date.now() - validationStartTime;
      
      console.log(`✅ Successfully seeded ${this.domainName} domain`);
      
      return this.createSuccessResponse({
        totalCollections: 4, // domains, bounded_contexts, aggregates, package_mappings
        totalDocuments: validationResult.totalDocuments,
        validDocuments: validationResult.validDocuments,
        invalidDocuments: validationResult.invalidDocuments,
        seedingDurationMs,
        validationDurationMs
      });
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`❌ Domain seeding failed for ${this.domainName}:`, errorMessage);
      
      return this.createErrorResponse(errorMessage, {
        totalCollections: 0,
        totalDocuments: 0,
        validDocuments: 0,
        invalidDocuments: 0,
        seedingDurationMs: Date.now() - seedingStartTime,
        validationDurationMs: 0
      });
    }
  }

  async validateSeededData(): Promise<{
    isValid: boolean;
    totalDocuments: number;
    validDocuments: number;
    invalidDocuments: number;
    errors: string[];
  }> {
    try {
      const mongoClient = getMongoClient();
      const config = getMongoConfig();
      const db = mongoClient.db(config?.dbName);
      
      const projectId = 'c3f0fb44-79da-4200-b6c8-bd30ab57a6db'; // Default project ID
      const collections = Object.values(DDD_COLLECTIONS);
      
      let totalDocuments = 0;
      let validDocuments = 0;
      let invalidDocuments = 0;
      const errors: string[] = [];
      
      for (const collectionName of collections) {
        try {
          const coll = db.collection(collectionName);
          const docs = await coll.find({ projectId }).toArray();
          
          totalDocuments += docs.length;
          
          for (const doc of docs) {
            const validation = this.validateDocument(doc, collectionName);
            if (validation.isValid) {
              validDocuments++;
            } else {
              invalidDocuments++;
              errors.push(...validation.errors);
            }
          }
          
          console.log(`✅ Validated ${collectionName}: ${docs.length} documents`);
        } catch (error) {
          errors.push(`Failed to validate ${collectionName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
      
      return {
        isValid: errors.length === 0,
        totalDocuments,
        validDocuments,
        invalidDocuments,
        errors
      };
      
    } catch (error) {
      return {
        isValid: false,
        totalDocuments: 0,
        validDocuments: 0,
        invalidDocuments: 0,
        errors: [error instanceof Error ? error.message : 'Unknown validation error']
      };
    }
  }

  private validateDocument(doc: any, collectionName: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Basic validation - ensure required fields exist
    if (!doc.id) {
      errors.push(`Document in ${collectionName} missing required field: id`);
    }

    // Collection-specific validation
    switch (collectionName) {
      case DDD_COLLECTIONS.DOMAINS:
        if (!doc.domainId || !doc.name || !doc.description || !doc.vision) {
          errors.push(`Domain document missing required fields`);
        }
        break;

      case DDD_COLLECTIONS.BOUNDED_CONTEXTS:
        if (!doc.boundedContextId || !doc.name || !doc.description || !doc.domainId) {
          errors.push(`Bounded context document missing required fields`);
        }
        break;

      case DDD_COLLECTIONS.AGGREGATES:
        if (!doc.aggregateId || !doc.name || !doc.description || !doc.boundedContextId) {
          errors.push(`Aggregate document missing required fields`);
        }
        break;

      case DDD_COLLECTIONS.PACKAGE_MAPPINGS:
        if (!doc.mappingId || !doc.packageName || !doc.packageType || !doc.domainId) {
          errors.push(`Package mapping document missing required fields`);
        }
        break;
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

/**
 * Factory function to create DDD domain seeder
 */
export function createDDDSeeder(): DDDSeeder {
  return new DDDSeeder();
}